import { Ambassador, AmbassadorCluster } from '../types';
import L from 'leaflet';

/**
 * Groups ambassadors by their coordinates into clusters
 */
export function createAmbassadorClusters(ambassadors: Ambassador[], tolerance: number = 0.001): AmbassadorCluster[] {
  const clusters = new Map<string, Ambassador[]>();
  
  ambassadors.forEach(ambassador => {
    if (ambassador.latitude && ambassador.longitude) {
      // Round coordinates to create groups of nearby ambassadors
      const lat = Math.round(ambassador.latitude / tolerance) * tolerance;
      const lng = Math.round(ambassador.longitude / tolerance) * tolerance;
      const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
      
      if (!clusters.has(key)) {
        clusters.set(key, []);
      }
      clusters.get(key)!.push(ambassador);
    }
  });
  
  return Array.from(clusters.entries()).map(([key, ambassadors]) => {
    const [lat, lng] = key.split(',').map(Number);
    
    return {
      position: [lat, lng] as [number, number],
      ambassadors,
      isCluster: ambassadors.length > 1,
      city: ambassadors[0].city,
      country: ambassadors[0].country,
    };
  });
}

/**
 * Creates a cluster icon with count badge
 */
export function createClusterIcon(cluster: AmbassadorCluster): L.DivIcon {
  const count = cluster.ambassadors.length;
  const isLarge = count >= 10;
  
  if (!cluster.isCluster) {
    // Return individual ambassador icon (we'll handle this in the map component)
    return L.divIcon({
      html: '',
      className: 'individual-marker',
    });
  }
  
  const sizeClass = isLarge ? 'large' : '';
  
  return L.divIcon({
    html: `
      <div class="cluster-marker ${sizeClass}">
        <div class="cluster-count">${count}</div>
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: isLarge ? [38, 38] : [32, 32],
    iconAnchor: isLarge ? [19, 19] : [16, 16],
  });
}

/**
 * Checks if two coordinates are within tolerance distance
 */
export function areCoordinatesClose(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  tolerance: number = 0.001
): boolean {
  return Math.abs(lat1 - lat2) < tolerance && Math.abs(lng1 - lng2) < tolerance;
}

/**
 * Finds all ambassadors at a specific location
 */
export function findAmbassadorsAtLocation(
  ambassadors: Ambassador[],
  targetLat: number,
  targetLng: number,
  tolerance: number = 0.001
): Ambassador[] {
  return ambassadors.filter(ambassador =>
    ambassador.latitude &&
    ambassador.longitude &&
    areCoordinatesClose(ambassador.latitude, ambassador.longitude, targetLat, targetLng, tolerance)
  );
}